import { useState } from "react";

import { useKioskSaveAttendanceMutation } from "../api/klassenbuchKioskApi";
import type {
  AttendanceStatus,
  ClassWeekCacheKey,
  KioskClassWeekAbsentee,
  KioskClassWeekCell,
  KioskLessonBundle,
} from "../types/kioskApiTypes";

/**
 * useKioskConfirmAttendance — quick "Bestätigen" flow for the CellAttendance
 * component. Confirms the carry-forward absentee list for a cell without
 * opening the full AttendancePanel.
 */
export function useKioskConfirmAttendance(
  cell: KioskClassWeekCell,
  userId: string,
  classWeekKey: ClassWeekCacheKey,
) {
  const [confirm, { isLoading: isConfirming }] = useKioskSaveAttendanceMutation();
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const handleConfirm = async (list: KioskClassWeekAbsentee[]) => {
    setConfirmError(null);
    try {
      await confirm({
        lessonId: cell.lesson_id,
        schoolId: cell.school_id,
        userId,
        attendance: list.map((a) => ({
          student_id: a.student_id,
          status: a.status,
          note: a.notes ?? null,
          late_minutes: a.late_minutes ?? null,
        })),
        _classWeekKey: classWeekKey,
      }).unwrap();
    } catch (e) {
      const msg =
        (e as { error?: { message?: string }; message?: string })?.error?.message ??
        (e as { message?: string })?.message ??
        "Bestätigen fehlgeschlagen";
      setConfirmError(msg);
    }
  };

  return { handleConfirm, isConfirming, confirmError };
}

interface UseKioskSaveFullAttendanceOpts {
  bundle: KioskLessonBundle | undefined;
  userId: string;
  classWeekKey: ClassWeekCacheKey;
  statusMap: Record<string, AttendanceStatus>;
  notesMap: Record<string, string>;
  lateMinutesMap: Record<string, string>;
  onClose: () => void;
}

/**
 * useKioskSaveFullAttendance — full panel save for AttendancePanel.
 * Builds the attendance payload from the component's status/notes/lateMinutes
 * maps and dispatches the mutation with a cache-patching key.
 */
export function useKioskSaveFullAttendance(opts: UseKioskSaveFullAttendanceOpts) {
  const [saveAttendance, { isLoading: isSaving }] = useKioskSaveAttendanceMutation();
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    if (!opts.bundle) return;
    setSaveError(null);
    try {
      await saveAttendance({
        lessonId: opts.bundle.lesson_id,
        schoolId: opts.bundle.school_id,
        userId: opts.userId,
        attendance: opts.bundle.students.map((s) => {
          const status: AttendanceStatus = opts.statusMap[s.id] ?? "present";
          // Only attach a note for excused absence (Inf) or late.
          // late_minutes only sent for late.
          const note =
            status === "absent_excused" || status === "late"
              ? (opts.notesMap[s.id]?.trim() || null)
              : null;
          const lateMins =
            status === "late"
              ? (() => {
                  const raw = opts.lateMinutesMap[s.id];
                  const n = raw != null && raw !== "" ? parseInt(raw, 10) : NaN;
                  return Number.isFinite(n) && n > 0 ? n : null;
                })()
              : null;
          return { student_id: s.id, status, note, late_minutes: lateMins };
        }),
        _classWeekKey: opts.classWeekKey,
      }).unwrap();
      opts.onClose();
    } catch (e) {
      const msg =
        (e as { error?: { message?: string }; message?: string })?.error?.message ??
        (e as { message?: string })?.message ??
        "Speichern fehlgeschlagen";
      setSaveError(msg);
    }
  };

  return { save, isSaving, saveError };
}
